/**
 * LockScreen — full-screen overlay shown after inactivity. Unlocks by PIN /
 * password (POST /api/security/verify-unlock) or by Google re-authentication.
 */

import React, { useState } from 'react';
import { Box, Paper, TextField, Button, Typography, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiServices';

export default function LockScreen({ user, onUnlocked }) {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const name = user?.data?.firstname
    ? `${user.data.firstname} ${user.data.lastname || ''}`.trim()
    : (user?.data?.email || '');

  const submit = async (e) => {
    e.preventDefault();
    if (!secret || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await authFetch('/api/security/verify-unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success) {
        setSecret('');
        onUnlocked();
      } else {
        setError(t('login.lockWrong'));
      }
    } catch {
      setError(t('login.lockWrong'));
    } finally {
      setBusy(false);
    }
  };

  const reauthGoogle = () => {
    const api = process.env.REACT_APP_API_URL || '';
    if (window.electronAPI?.openExternal) {
      window.electronAPI.openExternal(`${api}/api/auth/google/start?surface=desktop`);
    } else {
      window.location.href = `${api}/api/auth/google/start?surface=web`;
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 21000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'rgba(15,23,42,0.78)', backdropFilter: 'blur(8px)',
      }}
    >
      <Paper elevation={8} sx={{ width: 'min(92vw, 380px)', p: { xs: 3, md: 4 }, borderRadius: '18px', textAlign: 'center' }}>
        <Box sx={{ width: 56, height: 56, mx: 'auto', mb: 2, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef6f8' }}>
          <LockOutlinedIcon sx={{ color: '#256878', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a' }}>{t('login.lockTitle')}</Typography>
        {name && <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>{name}</Typography>}
        <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 1, mb: 2.5 }}>{t('login.lockSubtitle')}</Typography>

        <Box component="form" onSubmit={submit}>
          <TextField
            type="password"
            autoFocus
            fullWidth size="small"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            label={t('login.lockPinLabel')}
            error={!!error}
            helperText={error || ' '}
            sx={{ mb: 1 }}
          />
          <Button
            type="submit" fullWidth variant="contained" disabled={busy || !secret}
            sx={{ py: 1.2, borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)' }}
          >
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('login.lockUnlock')}
          </Button>
        </Box>

        <Button
          fullWidth variant="text"
          onClick={reauthGoogle}
          startIcon={<GoogleIcon sx={{ fontSize: 18, color: '#4285F4' }} />}
          sx={{ mt: 1.5, textTransform: 'none', color: '#475569', fontWeight: 600 }}
        >
          {t('login.lockReauth')}
        </Button>
      </Paper>
    </Box>
  );
}
